import PropTypes from 'prop-types'
import styled from 'styled-components'

import { useEffect, useRef, useState } from 'react'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import { Panel } from 'components/Panel'
import ColumnGridPanel from 'components/ColumnGridPanel'

import IndoorUnit from 'pages/Device/SystemStatusPanel/IndoorUnit'
import LastUpdated from 'pages/Device/LastUpdated'
import OutdoorUnit from 'pages/Device/SystemStatusPanel/OutdoorUnit'
import Settings from 'pages/Device/SystemStatusPanel/Settings'
import Status from 'pages/Device/SystemStatusPanel/Status'
import ThermostatBox from 'pages/Device/SystemStatusPanel/ThermostatBox'
import ZoningInfo from 'pages/Device/SystemStatusPanel/ZoningInfo'

import { NoData } from 'styles/shared'
import { color } from 'styles/colors'

const propTypes = {
  report: PropTypes.shape(),
  hasAwairDevice: PropTypes.bool.isRequired
}

const defaultProps = {
  report: null
}

const SystemStatusPanel = ({ report, hasAwairDevice }) => {
  const [isPanelExpanded, setIsPanelExpanded] = useState(true)
  const [hideShowMore, setHideShowMore] = useState(true)
  const gridRef = useRef(null)

  useEffect(() => {
    if (gridRef.current) {
      const maxNumberOfGridFields = _.max([...gridRef.current.children].map((listItem) => listItem.children.length))
      setHideShowMore(maxNumberOfGridFields < 13) // show show more button if elements count is more than 12 including column header in any column
    }
  }, [report])

  return (
    <S.Panel pingdomId="thermostat-current-status-container" id="system-status" scrollToSection>
      <Panel.Header>
        <Panel.Title>System Status</Panel.Title>
        {report && <LastUpdated date={report.lastUpdatedAt} /> }
      </Panel.Header>
      {
        !report
          ? <S.NoData><h1>No Data Received Yet</h1></S.NoData>
          : (
            <S.PanelContainer>
              <S.ColumnGridPanel isPanelExpanded={isPanelExpanded} ref={gridRef}>
                {
                  !_.isEmpty(report.zones) &&
                  report.zones.length === 1 &&
                  <ThermostatBox {...report.zones[0]} mode={report.systemMode} outdoorTemperature={report.outdoorTemperature} hasAwairDevice={hasAwairDevice} />
                }

                <Settings report={report} />
                <Status report={report} />
                <OutdoorUnit report={report} />
                <IndoorUnit report={report} />
                {
                  !_.isEmpty(report.zones) &&
                  report.zones.length > 1 &&
                  <ZoningInfo report={report} />
                }
              </S.ColumnGridPanel>

              {
                !hideShowMore && (
                  <Button
                    decoration="secondary"
                    text={isPanelExpanded ? 'Show More' : 'Show Less'}
                    styles={{ button: { marginTop: 10 } }}
                    onPress={() => setIsPanelExpanded(!isPanelExpanded)}
                  />
                )
              }
            </S.PanelContainer>
          )
      }
    </S.Panel>
  )
}

SystemStatusPanel.propTypes = propTypes
SystemStatusPanel.defaultProps = defaultProps

export default SystemStatusPanel

const S = {
  Panel: styled(Panel)`
    scroll-margin-top: 60px;
    background-color: ${color('paneBackground')};
  `,
  NoData: styled(NoData)`
    h1 {
      font-size: 28px;
    }
  `,
  PanelContainer: styled.div`
    display: flex;
    flex-direction: column;
  `,
  ColumnGridPanel: styled(ColumnGridPanel)`
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));

    > div > :nth-child(n+12) {
      display: ${({ isPanelExpanded }) => isPanelExpanded ? 'none' : 'grid'};
    }
  `
}
